import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfo } from "@fortawesome/free-solid-svg-icons";
import { getRequest, postRequest } from "../../utils/axios";
import { NotificationManager } from "react-notifications";
import moment from "moment";

import MainA from "../../layout/Main Admin";
import Button from "../../components/atoms/Button";
import Loader from "../../components/atoms/Loader";
import Table from "../../components/atoms/Table";
import Modal from "../../components/atoms/Modal";
import CustomDropdown from "../../components/atoms/CustomDropdown";
import Input from "../../components/atoms/Input";

import "./styles.scss";

const baseUrl = "https://dynamicproductstorage.blob.core.windows.net/profile/";

const EventParticipantRequest = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState([]);
  const [sortColumn, setSortColumn] = useState("requestType");
  const [sortOrder, setSortOrder] = useState("asc");
  const [eventData, setEventData] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState({
    label: "All Events",
    value: "",
  });
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [showUserModal, setShowUserModal] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const [ticketName, setTicketName] = useState("");
  const [completionTime, setCompletionTime] = useState("");
  const [bibNo, setBibNo] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectRequestId, setRejectRequestId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteRequestId, setDeleteRequestId] = useState(null);

  useEffect(() => {
    fetchRequests();
    getEvents();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);

      const resp = await getRequest(
        "EventParticipantRequest/GetEventParticipantRequestByStatus/Requested",
      );

      setRequestData(resp.data || []);
      setFilteredRequests(resp.data || []);
    } catch (error) {
      console.error(error);
      NotificationManager.error("Failed to fetch requests");
    } finally {
      setLoading(false);
    }
  };

  const getEvents = async () => {
    try {
      const resp = await getRequest(
        `Page/GetPagesByCategoryType/${categoryId}`,
      );
      setEventData(resp.data);
    } catch (error) {
      console.error("Error fetching event:", error);
    }
  };

  const handleSort = (column) => {
    const order = sortColumn === column && sortOrder === "asc" ? "desc" : "asc";

    setSortColumn(column);
    setSortOrder(order);

    const sorted = [...requestData].sort((a, b) => {
      const aVal = a[column] || "";
      const bVal = b[column] || "";
      return order === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });

    setRequestData(sorted);
    applyFilters(selectedEvent.value, searchText, sorted);
  };

  const handleEventFilter = (eventId) => {
    const selected = eventId
      ? {
          label:
            eventData.find((e) => String(e.pageId) === String(eventId))
              ?.pageName || "Event",
          value: eventId,
        }
      : { label: "All Events", value: "" };

    setSelectedEvent(selected);
    applyFilters(eventId, searchText);
  };

  const applyFilters = (eventId, text, sourceData = requestData) => {
    let data = [...sourceData];

    if (eventId) {
      data = data.filter((item) => String(item.pageId) === String(eventId));
    }

    if (text) {
      const lower = text.toLowerCase();

      data = data.filter((item) => {
        const eventMap = Object.fromEntries(
          eventData.map(e => [e.pageId, e.pageName])
        );

        const eventName = eventMap[item.pageId] || "";

        return (
          item.userFullName?.toLowerCase().includes(lower) ||
          item.userEmail?.toLowerCase().includes(lower) ||
          item.phoneNo?.toLowerCase().includes(lower) ||
          String(item.bibNo || "")
            .toLowerCase()
            .includes(lower) ||
          eventName.toLowerCase().includes(lower)
        );
      });
    }

    setFilteredRequests(data);
  };

  const handleSearchChange = (value) => {
    setSearchText(value);
    applyFilters(selectedEvent.value, value);
  };

  const handleApproveSubmit = async () => {
    if (!ticketName) {
      NotificationManager.warning("Please fill all fields");
      return;
    }

    try {
      setApproveLoading(true);
      const postData = {
        participantName: selectedRequest.userFullName,
        pageId: selectedRequest.pageId,
        email: selectedRequest.userEmail,
        phoneNo: selectedRequest.phoneNo,
        ticketName: ticketName,
        completionTime: completionTime,
        bibNo: bibNo,
      };

      await postRequest(
        `EventParticipantRequest/ApproveRequest/${selectedRequest.eventRequestId}`,
        postData,
      );

      NotificationManager.success("Request approved successfully");

      setShowApproveModal(false);
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      console.error("Error approving request:", error);
      NotificationManager.error("Failed to approve request");
    } finally {
      setApproveLoading(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectComment.trim()) {
      NotificationManager.warning("Please add a rejection comment");
      return;
    }

    try {
      setRejectLoading(true);

      await postRequest(`EventParticipantRequest/RejectRequest`, {
        comment: rejectComment,
        eventRequestId: rejectRequestId,
      });

      NotificationManager.success("Request rejected successfully");
      setShowRejectModal(false);
      fetchRequests();
    } catch (error) {
      console.error("Error rejecting request:", error);
      NotificationManager.error("Failed to reject request");
    } finally {
      setRejectLoading(false);
    }
  };

  const handleRejectClick = (id) => {
    setRejectRequestId(id);
    setRejectComment("");
    setShowRejectModal(true);
  };

  const handleDeleteClick = (id) => {
    setDeleteRequestId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteRequestId) return;

    try {
      setDeleteLoading(true);

      await getRequest(`EventParticipantRequest/DeleteRequest/${deleteRequestId}`);

      NotificationManager.success("Request deleted successfully");
      setShowDeleteModal(false);
      setDeleteRequestId(null);
      fetchRequests();
    } catch (error) {
      console.error("Error deleting request:", error);
      NotificationManager.error("Failed to delete request");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleViewDetails = async (data) => {
    setShowUserModal(true);
    setUserDetails(data);
  };

  const handleCloseUserModal = () => setShowUserModal(false);

  return (
    <MainA className="b-event-participant-request">
      {loading && <Loader loading={loading} />}

      <Modal isOpen={showUserModal} closeModal={handleCloseUserModal}>
        {userDetails ? (
          <div className="user-data">
            <img
              className="user-image"
              src={baseUrl + userDetails.profileImage}
              alt="User"
            />
            <div className="user-details">
              <p>
                <strong>Name:</strong> {userDetails.userFullName || "-"}
              </p>
              <p>
                <strong>Email:</strong> {userDetails.userEmail || "—"}
              </p>
              <p>
                <strong>Phone:</strong> {userDetails.phoneNo || "—"}
              </p>
              <p>
                <strong>Event:</strong> {userDetails.eventName || "—"}
              </p>
              <p>
                <strong>BibNo:</strong> {userDetails.bibNo || "—"}
              </p>
            </div>
          </div>
        ) : (
          <p>No user data found</p>
        )}
      </Modal>

      <Modal
        isOpen={showDeleteModal}
        closeModal={() => setShowDeleteModal(false)}
      >
        <h2>Confirm Delete</h2>

        <div className="delete-confirm">
          <p>Are you sure you want to delete this request?</p>

          <div className="modal-actions">
            <Button
              className="secondary-btn"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>

            <Button
              className="reject-btn"
              onClick={handleDeleteConfirm}
              disabled={deleteLoading}
            >
              {deleteLoading ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showApproveModal}
        closeModal={() => setShowApproveModal(false)}
      >
        <h2>Approve Request</h2>

        <div className="approve-form">
          <div>
            <label>Ticket Name*</label>
            <Input
              type="text"
              placeholder="Enter ticket name"
              value={ticketName}
              onChange={(e) => setTicketName(e.target.value)}
            />
          </div>

          <div>
            <label>Completion Time</label>
            <Input
              type="text"
              placeholder="Enter completion time"
              value={completionTime}
              onChange={(e) => setCompletionTime(e.target.value)}
            />
          </div>

          <div className="modal-actions">
            <Button
              className="secondary-btn"
              onClick={() => setShowApproveModal(false)}
            >
              Cancel
            </Button>

            <Button
              className="approve-btn"
              onClick={handleApproveSubmit}
              disabled={approveLoading}
            >
              {approveLoading ? "Approving..." : "Confirm Approve"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showRejectModal}
        closeModal={() => setShowRejectModal(false)}
      >
        <h2>Reject Request</h2>

        <div className="reject-form">
          <label>Reason for Rejection</label>
          <Input
            type="text"
            placeholder="Enter rejection reason"
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
          />

          <div className="modal-actions">
            <Button
              className="secondary-btn"
              onClick={() => setShowRejectModal(false)}
            >
              Cancel
            </Button>

            <Button
              className="reject-btn"
              onClick={handleRejectSubmit}
              disabled={rejectLoading}
            >
              {rejectLoading ? "Rejecting..." : "Confirm Reject"}
            </Button>
          </div>
        </div>
      </Modal>

      <div className="gallery-wrapper">
        <div className="event-header">
          <h1>Event Participant Requests</h1>

          <div className="filter">
            <Input
              className="search-input"
              type="search"
              placeholder="Search"
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
            <CustomDropdown
              options={[
                { label: "All Events", value: "" },
                ...eventData.map((event) => ({
                  label: event.pageName,
                  value: event.pageId,
                })),
              ]}
              className="event-dropdown"
              onSelect={(option) => handleEventFilter(option.value)}
              value={selectedEvent}
            />
          </div>
        </div>
        <div className="requests-table">
          <Table
            pagination={true}
            pageSize={10}
            totalRows={filteredRequests.length}
            categoryName="reel-requests"
          >
            <thead>
              <tr>
                <th onClick={() => handleSort("participantName")}>
                  Participant Name
                </th>
                <th onClick={() => handleSort("pageId")}>Event</th>
                <th>Event Date</th>
                <th onClick={() => handleSort("bibNo")}>Bib Id</th>
                <th onClick={() => handleSort("email")}>Email</th>
                <th onClick={() => handleSort("phoneNo")}>Phone No</th>
                <th onClick={() => handleSort("requestDateTime")}>Request Date</th>
                <th></th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredRequests.length ? (
                [...filteredRequests].sort((a, b) => new Date(b.requestDateTime) - new Date(a.requestDateTime)).map((item) => (
                  <tr key={item.eventRequestId}>
                    <td>{item.userFullName}</td>
                    <td>
                      <a
                        href={`/events/clips/${item.pageId}?categoryId=${categoryId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.eventName}
                      </a>
                    </td>
                    <td>
                      {moment(
                        eventData.find((event) => event.pageId === item.pageId)
                          ?.publishDate,
                      ).format("MMMM DD, YYYY")}
                    </td>
                    <td>{item.bibNo ?? "—"}</td>
                    <td>{item.userEmail ?? "—"}</td>
                    <td>{item.phoneNo ?? "—"}</td>
                    <td>
                      {moment(item.requestDateTime).format("MMMM DD, YYYY")}
                    </td>
                    <td>
                      <FontAwesomeIcon
                        icon={faInfo}
                        className="info-icon"
                        onClick={() => handleViewDetails(item)}
                      />
                    </td>
                    <td>
                      <div className="actions">
                        {item.isParticipantExists === 1 && (
                          <Button
                            className="reject-btn"
                            onClick={() => handleDeleteClick(item.eventRequestId)}
                          >
                            Delete
                          </Button>
                        )}
                        <Button
                          className="approve-btn"
                          onClick={() => {
                            setSelectedRequest(item);
                            setTicketName("");
                            setCompletionTime("");
                            setBibNo(item.bibNo);
                            setShowApproveModal(true);
                          }}
                        >
                          Approve
                        </Button>

                        <Button
                          className="reject-btn"
                          onClick={() => handleRejectClick(item.eventRequestId)}
                        >
                          Reject
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>
                    No requests found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </MainA>
  );
};

export default EventParticipantRequest;
